// @flow
import * as React from 'react';
import classnames from 'classnames';
import {
    Brush as RechartsBrush,
    Layer as RechartsLayer,
    Text as RechartsText
} from 'recharts';
import theme from './Brush.scss';
import _ from 'lodash';

class Brush extends RechartsBrush {
    static defaultProps = {
        stroke: '#bbbbbb',
        height: 30,
        travellerWidth: 10,
        fill: 'none'
    };

    _renderCustomText(type, textRenderer, props, value, x) {
        const { y, height, stroke } = this.props;
        const attrs = {
            pointerEvents: 'none',
            fill: stroke
        };
        let text;
        const textProps = { ...props, value, type, x, y, height };
        if (React.isValidElement(textRenderer)) {
            text = React.cloneElement(textRenderer, textProps);
        } else if (_.isFunction(textRenderer)) {
            text = textRenderer(textProps);
        } else {
            text = (
                <RechartsText
                    textAnchor="middle"
                    verticalAnchor="middle"
                    x={x}
                    y={y + height + 5}
                    {...attrs}
                >
                    {value}
                </RechartsText>
            );
        }
        return text;
    }

    _renderText() {
        const {
            startIndex,
            endIndex,
            travellerWidth,
            textRenderer,
            textProps
        } = this.props;
        const { startX, endX } = this.state;

        return (
            <RechartsLayer className="recharts-brush-texts">
                {this._renderCustomText(
                    'START',
                    textRenderer,
                    textProps,
                    this.getTextOfTick(startIndex),
                    Math.min(startX, endX)
                )}
                {this._renderCustomText(
                    'END',
                    textRenderer,
                    textProps,
                    this.getTextOfTick(endIndex),
                    Math.max(startX, endX) + travellerWidth
                )}
            </RechartsLayer>
        );
    }

    _renderTraveller(startX, id) {
        const { y, height, stroke, travellerWidth } = this.props;
        const x = Math.max(startX, this.props.x);

        const lineY = Math.floor(y + height / 2) - 1;
        return (
            <RechartsLayer
                className={classnames(
                    theme.brushHandle,
                    'recharts-brush-traveller'
                )}
                onMouseEnter={this.handleEnterSlideOrTraveller}
                onMouseLeave={this.handleLeaveSlideOrTraveller}
                onMouseDown={this.travellerDragStartHandlers[id]}
                onTouchStart={this.travellerDragStartHandlers[id]}
                style={{ cursor: 'col-resize' }}
            >
                <rect
                    className={theme.brushHandleBg}
                    x={x}
                    y={y}
                    width={travellerWidth}
                    height={height}
                    fill={stroke}
                    stroke="none"
                />
                <RechartsLayer className={theme.brushLines}>
                    <line
                        x1={x + 3}
                        y1={lineY}
                        x2={x + travellerWidth - 3}
                        y2={lineY}
                        fill="none"
                    />
                    <line
                        x1={x + 3}
                        y1={lineY + 2}
                        x2={x + travellerWidth - 3}
                        y2={lineY + 2}
                        fill="none"
                    />
                    <line
                        x1={x + 3}
                        y1={lineY + 4}
                        x2={x + travellerWidth - 3}
                        y2={lineY + 4}
                        fill="none"
                    />
                </RechartsLayer>
            </RechartsLayer>
        );
    }

    render() {
        const { data, className, children } = this.props;
        const {
            startX,
            endX,
            isTextActive,
            isSlideMoving,
            isTravellerMoving
        } = this.state;

        if (!data || !data.length) {
            return null;
        }

        const layerClass = classnames(
            'recharts-brush',
            theme.rechartsBrush,
            className
        );
        const isPanoramic = React.Children.count(children) === 1;
        return (
            <RechartsLayer
                className={layerClass}
                onMouseMove={this.handleDrag}
                onMouseLeave={this.handleLeaveWrapper}
                onMouseUp={this.handleDragEnd}
                onTouchEnd={this.handleDragEnd}
                onTouchMove={this.handleTouchMove}
            >
                {this.renderBackground()}
                {isPanoramic && this.renderPanorama()}
                {this.renderSlide(startX, endX)}
                {this._renderTraveller(startX, 'startX')}
                {this._renderTraveller(endX, 'endX')}
                {(isTextActive || isSlideMoving || isTravellerMoving) &&
                    this._renderText()}
            </RechartsLayer>
        );
    }
}

export default Brush;
